import React from 'react'
import { tokens } from "../../../theme";
import { Box, useTheme, Typography, Tooltip } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '../../../components/alert/Alert';
import Header from "../../../components/Header";
import AdminActionAccordion from './AdminActionAccordion';
import { useOutletContext } from 'react-router-dom';
import ModuleBox from './ModuleBox';
import EmptyModuleBox from './EmptyModuleBox';
import dayjs from 'dayjs';
import getCookies from '../../../utils/getCookies';
import Loading from '../../../components/Loading';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Axios from 'axios';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import BulkDeleteModulesModal from './BulkDeleteModulesModal';
import { getAuthHeader } from '../../../utils/authToken';

const Modules = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const matches = useMediaQuery('(min-width:1500px)');
    const matches2 = useMediaQuery('(min-width:1200px)');
    const matches3 = useMediaQuery('(min-width:1100px)');

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    const [loading, setLoading] = React.useState(true);
    const [courseId, setCourseId] = React.useState(null);
    const courseData = useOutletContext().courseData;
    const eventModules = useOutletContext().lectureModules;
    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const [zooms, setZooms] = React.useState(null);

    // Total class session count (set by admin in the weekly view) vs. modules created so far.
    // Only admins/teachers see the totals and the placeholder boxes.
    const canSeeTotals = userRole === "admin" || userRole === "teacher";
    const moduleCount = eventModules?.length || 0;
    const totalSessions = courseData?.private_total_classes || 0;
    // Canceled modules keep their number without consuming one (see server helpers/module_helper.js:
    // "Class Module 1, 2, 3 (canceled), 3, 4"), so the highest real module number equals the count
    // of NON-canceled modules. Match the server's cancel check: is_canceled OR is_canceled_int > 0.
    const isCanceled = (m) => m.is_canceled === true || (m.is_canceled_int || 0) > 0;
    const activeModuleCount = (eventModules || []).filter((m) => !isCanceled(m)).length;
    // Modules "done" = non-canceled sessions that have already ended.
    const modulesDone = (eventModules || []).filter(
        (m) => !isCanceled(m) && m.end && dayjs(m.end).isBefore(dayjs())
    ).length;
    // Placeholders fill the remaining sessions with their natural next numbers, descending so the
    // highest sits at the top (e.g. 20, 19 when there are 18 active modules of 20).
    const emptyModuleNumbers = (canSeeTotals && moduleCount > 0 && totalSessions > activeModuleCount)
        ? Array.from({ length: totalSessions - activeModuleCount }, (_, i) => totalSessions - i)
        : [];

    // for backdrop
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const handleBackdropClose = () => {
        setBackdropOpen(false);
    };

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    // useEffect
    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Modules');
        setCourseId(window.location.pathname.split('/')[2]);

        setLoading(false);
    }, []);

    React.useEffect(() => {
        const fetchZooms = async () => {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/zooms`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                setZooms(response.data);
            }).catch((error) => {
                if (error.response?.status === 404) {
                    setZooms([]);
                    return;
                } else if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
        };

        fetchZooms();
    }, []);


    return (
        <Box m="20px" marginX={3} marginY={3} minHeight="90%">
            { loading || courseData === null || eventModules === null ? 
                <Loading />
                : <>
                    <Box 
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            px: "25%",
                            ...!matches && {
                                px: "15%",
                            },
                            ...!matches2 && {
                                px: "10%",
                            },
                            ...!matches3 && {
                                px: "0%",
                            },
                        }}
                    >
                        <Header 
                            title={
                                courseData.code !== null 
                                ? courseData.code + ". " + courseData.course_title 
                                : courseData.course_title
                            } 
                            subtitle="A ONE Institute"
                        />
                        {/* For Admins */}
                        {(userRole === "admin") && 
                            <Box
                                position="relative"
                                minWidth="20rem"
                                width="100%"
                                minHeight="10rem"
                                borderRadius="3px"
                                padding={3}
                                boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                                backgroundColor={colors.primary[600]}
                                display="flex"
                                flexDirection="column"
                                alignItems="left"
                                gap={3}
                                mb={3}
                            >
                                <BulkDeleteModulesModal 
                                    eventModules={eventModules} 
                                    userRole={userRole}
                                    setBackdropOpen={setBackdropOpen}
                                    setSuccessAlertOpen={setSuccessAlertOpen}
                                    setErrorAlertOpen={setErrorAlertOpen}
                                    setSuccessAlertMessage={setSuccessAlertMessage}
                                    setErrorAlertMessage={setErrorAlertMessage}
                                />
                                <Typography variant="h4" color={colors.primary[450]}>
                                    Admin Actions
                                </Typography>
                                <AdminActionAccordion 
                                    courseId={courseId} 
                                    courseData={courseData}
                                    setBackdropOpen={setBackdropOpen}
                                    setSuccessAlertOpen={setSuccessAlertOpen}
                                    setErrorAlertOpen={setErrorAlertOpen}
                                    setSuccessAlertMessage={setSuccessAlertMessage}
                                    setErrorAlertMessage={setErrorAlertMessage}
                                />
                            </Box>
                        }

                        {/* Lecture Modules */}
                        <Box
                            minWidth="20rem"
                            width="100%"
                            minHeight="32rem"
                            borderRadius="3px"
                            padding={8}
                            boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                            backgroundColor={colors.primary[600]}
                            alignItems="center"
                        >
                            <Box
                                display="flex"
                                alignItems="baseline"
                                gap={1.5}
                                borderBottom="0.1px solid"
                                mb={2}
                            >
                                <Typography variant="h3" color={colors.primary[450]}>
                                    Modules
                                </Typography>
                                {canSeeTotals && totalSessions ? (
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="h5" color={colors.primary[450]} sx={{ opacity: 0.75 }}>
                                            {modulesDone} / {totalSessions}
                                        </Typography>
                                        <Tooltip
                                            title={`${modulesDone} of ${totalSessions} class session(s) completed so far (sessions that have already ended, excluding canceled ones). Not-yet-created sessions appear as greyed-out placeholder boxes at the top. (Visible to admins and teachers only.)`}
                                            placement="top"
                                            arrow
                                        >
                                            <InfoOutlinedIcon
                                                sx={{ fontSize: "1rem", color: colors.primary[450], opacity: 0.75, cursor: "help" }}
                                            />
                                        </Tooltip>
                                    </Box>
                                ) : null}
                            </Box>
                            {eventModules === null || eventModules.length === 0 ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        minWidth: "20rem",
                                        minHeight: "16rem",
                                        gap: "0.4rem",
                                    }}
                                >
                                    <InfoOutlinedIcon sx={{ my: "auto" }} />
                                    <Typography variant="h4" color={colors.primary[450]} my="auto">
                                        No modules yet!
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                {emptyModuleNumbers.map((moduleNumber) => (
                                    <EmptyModuleBox key={`empty-${moduleNumber}`} moduleNumber={moduleNumber} />
                                ))}
                                {eventModules.map((eventModule, index) => {
                                    return (
                                        <ModuleBox 
                                            key={index}
                                            courseId={courseId}
                                            courseData={courseData}
                                            userRole={userRole}
                                            eventModule={eventModule} 
                                            eventModules_len={eventModules.length}
                                            zooms={zooms}
                                            index={index} 
                                            setBackdropOpen={setBackdropOpen}
                                            setSuccessAlertOpen={setSuccessAlertOpen}
                                            setErrorAlertOpen={setErrorAlertOpen}
                                            setSuccessAlertMessage={setSuccessAlertMessage}
                                            setErrorAlertMessage={setErrorAlertMessage}
                                        />
                                    )
                                })}</>
                            )}
                        </Box>
                    </Box>
                    <Alert
                        successAlertOpen={successAlertOpen}
                        setSuccessAlertOpen={setSuccessAlertOpen}
                        errorAlertOpen={errorAlertOpen}
                        setErrorAlertOpen={setErrorAlertOpen}
                        successMsg={successAlertMessage}
                        errorMsg={errorAlertMessage}
                        vertical="bottom"
                        horizontal="left"
                    />
                    <Backdrop
                        sx={{ color: colors.primary[400], zIndex: 2147483646 }}
                        open={backdropOpen}
                        onClick={handleBackdropClose}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>

                </>
            }
        </Box>
    )
}

export default Modules